import { useMemo } from "react";
import {
    IInteractionMetricsSummary,
    IInteractionsCount,
    IInteractionsGetQueryParameters,
    WritingIssueType,
} from "@edgetier/watchtower-types";
import {
    useInteractionMetricsSummaries,
    useInteractionMetricsSummary,
} from "hooks-for/interaction-metrics/use-interaction-metrics-summaries";
import useInteractionsCounts, {
    useInteractionsCount,
    useInteractionsMultipleCounts,
} from "hooks-for/interactions/use-interactions-counts";
import { PersistedStorageKeys } from "constants/persisted-storage-keys";
import { GRAMMAR_METRIC_ENABLED, SPELLING_METRIC_ENABLED } from "constants/feature-flags";
import createPersistedState from "use-persisted-state";
import { indexByGroup } from "utilities/index-by-group";
import { useInteractionQueryParameters } from "hooks-for/filters/use-interaction-query-parameters";
import useLookupQueryTags from "hooks-for/tags/use-lookup-query-tags";
import { decamelize } from "humps";
import { IComparisonData } from "./comparison-table.types";
import { isValueEmpty } from "utilities/is-value-empty";
import { calculatePercentageValue } from "utilities-for/formatting/calculate-percentage-value";

const useTagSelected = createPersistedState<string[]>(PersistedStorageKeys.TagSelected);

type IIndexedCounts<Group> = {
    [index: string]: IInteractionsCount<Group> | undefined;
};

/**
 * Format all the data necessary for comparison into one array.
 * @param summaries         An array of metric summaries grouped by agent ID.
 * @param counts            An array of interaction counts grouped by agent ID.
 * @param groups            An array of groups that will be used to create each row.
 * @param getGroupId        A function to get the ID of a group.
 * @param frustrationCounts An array with the number of interactions an agent has completed which contain frustration.
 * @param praiseCounts      An array with the number of interactions an agent has completed which contain praise.
 * @param grammarCounts     An array with the number of interactions an agent has completed which have grammar issues.
 * @param spellingCounts    An array with the number of interactions an agent has completed which have spelling issues.
 * @param multipleTagCounts An array with the number of interactions an agent has completed which contain certain tags.
 * @returns An array of formatted data suitable for agent comparison.
 */
const formatData = <Group>(
    summaries: { [index: string]: IInteractionMetricsSummary<string> | undefined },
    counts: IIndexedCounts<Group> | undefined,
    groups: Group[],
    getGroupId: (group: Group) => string,
    frustrationCounts: IIndexedCounts<Group> | undefined,
    praiseCounts: IIndexedCounts<Group> | undefined,
    grammarCounts: IIndexedCounts<Group> | undefined,
    spellingCounts: IIndexedCounts<Group> | undefined,
    multipleTagCounts: IIndexedCounts<Group>[] | undefined
) => {
    const formattedAgents = groups.reduce<IComparisonData<Group>[]>((formattedData, comparisonGroup) => {
        const key = getGroupId(comparisonGroup);
        // Find the data that corresponds to this group.
        const count = counts?.[key]?.count;

        if (typeof count === "undefined" || count === 0) {
            return formattedData;
        }

        const summary = summaries[key];
        const frustrationCount = frustrationCounts?.[key]?.count;
        const praiseCount = praiseCounts?.[key]?.count;
        const grammarCount = grammarCounts?.[key]?.count;
        const spellingCount = spellingCounts?.[key]?.count;

        const multiplePercentageTag = multipleTagCounts?.reduce<number[]>((values, currentValue) => {
            const tagCountValue = currentValue?.[key]?.count;
            const percentageValue = calculatePercentageValue(count, tagCountValue);
            return [...values, ...(typeof percentageValue === "number" ? [percentageValue] : [])];
        }, []);

        const percentageFrustration = calculatePercentageValue(count, frustrationCount);
        const percentagePraise = calculatePercentageValue(count, praiseCount);
        const percentageGrammar = calculatePercentageValue(count, grammarCount);
        const percentageSpelling = calculatePercentageValue(count, spellingCount);

        return [
            ...formattedData,
            {
                count: count ?? null,
                percentageFrustration,
                percentagePraise,
                percentageGrammar,
                percentageSpelling,
                comparisonGroup,
                groupId: key,
                tagValues: multiplePercentageTag,
                ...summary,
            },
        ];
    }, []);

    return formattedAgents;
};

/**
 * Request and format the data for overall interactions. This baseline data will be used to assess if an agent is
 * performing above or below average.
 * @param filters The filters to apply to the requests.
 * @returns The data for interactions overall.
 */
const useOverallComparisonDataQuery = <Group>(filters: IInteractionsGetQueryParameters) => {
    const { data: overallMetricsSummary, isLoading: isOverallDataLoading } = useInteractionMetricsSummary(filters);
    const { data: overallCount, isLoading: isOverallCountsLoading } = useInteractionsCount(filters);
    const [tagSelected] = useTagSelected([]);

    const { data: overallFrustrationCounts, isLoading: isOverallFrustrationCountsLoading } = useInteractionsCount({
        ...filters,
        hasFrustration: true,
    });

    const { data: overallPraiseCounts, isLoading: isOverallPraiseCountsLoading } = useInteractionsCount({
        ...filters,
        hasPraise: true,
    });

    const { data: overallGrammarCounts, isLoading: isOverallGrammarCountsLoading } = useInteractionsCount(
        {
            ...filters,
            writingIssueType: WritingIssueType.Grammar,
        },
        {
            enabled: GRAMMAR_METRIC_ENABLED,
        }
    );

    const { data: overallSpellingCounts, isLoading: isOverallSpellingCountsLoading } = useInteractionsCount(
        {
            ...filters,
            writingIssueType: WritingIssueType.Spelling,
        },
        {
            enabled: SPELLING_METRIC_ENABLED,
        }
    );

    const enableMultipleCounts =
        Array.isArray(tagSelected) && tagSelected.length > 0 && tagSelected.every((tag) => !isValueEmpty(tag));

    // Get all query tags including filter tags for each selection
    const lookupQueryTags = useLookupQueryTags(filters.tagId);
    const overallMultipleTagCounts = useInteractionsMultipleCounts(lookupQueryTags, filters, {
        enabled: enableMultipleCounts,
    });

    // Calculate overall percentage tag value for selected tags
    const overallPercentageTags = overallMultipleTagCounts.reduce<number[]>((values, { data }) => {
        const overallTagCountValue = data?.[0]?.count;
        const percentageValue = calculatePercentageValue(overallCount, overallTagCountValue);
        return [...values, ...(typeof percentageValue === "number" ? [percentageValue] : [])];
    }, []);

    const isOverallMultipleTagCountsLoading = useMemo(
        () => overallMultipleTagCounts.some(({ isLoading }) => isLoading),
        [overallMultipleTagCounts]
    );

    const overallData = useMemo(() => {
        if (typeof overallMetricsSummary === "undefined" || typeof overallCount === "undefined") {
            return undefined;
        }
        const overall: IComparisonData<Group> = {
            ...overallMetricsSummary,
            count: overallCount,
            isOverall: true,
            groupId: "Overall",
            comparisonGroup: null,
            percentageFrustration: calculatePercentageValue(overallCount, overallFrustrationCounts),
            percentagePraise: calculatePercentageValue(overallCount, overallPraiseCounts),
            percentageGrammar: calculatePercentageValue(overallCount, overallGrammarCounts),
            percentageSpelling: calculatePercentageValue(overallCount, overallSpellingCounts),
            tagValues: overallPercentageTags,
        };

        return overall;
    }, [
        overallMetricsSummary,
        overallCount,
        overallPraiseCounts,
        overallFrustrationCounts,
        overallGrammarCounts,
        overallSpellingCounts,
        overallPercentageTags,
    ]);

    return {
        data: overallData,
        isLoading:
            isOverallDataLoading ||
            isOverallCountsLoading ||
            isOverallFrustrationCountsLoading ||
            isOverallPraiseCountsLoading ||
            isOverallGrammarCountsLoading ||
            isOverallSpellingCountsLoading ||
            isOverallMultipleTagCountsLoading,
    };
};

/**
 * Request and format the data for that will be used to compare each group.
 * @param filters The filters to apply to the requests.
 * @returns The data for interactions grouped based on groupBy.
 */
const useComparisonDataQuery = <Group>(
    groups: Group[] | null,
    groupBy: string,
    getGroupId: (group: Group) => string,
    filters: IInteractionsGetQueryParameters
) => {
    const [tagSelected] = useTagSelected([]);

    const groupByFilters = { groupBy: decamelize(groupBy), ...filters };

    const { data: metricsSummaries, isLoading: isMetricsSummariesLoading } = useInteractionMetricsSummaries(
        groupByFilters,
        { select: indexByGroup }
    );

    const { data: counts, isLoading: isCountsLoading } = useInteractionsCounts(groupByFilters, {
        select: indexByGroup,
    });

    const { data: frustrationCounts, isLoading: isFrustrationCountsLoading } = useInteractionsCounts(
        {
            ...groupByFilters,
            hasFrustration: true,
        },
        { select: indexByGroup }
    );

    const { data: praiseCounts, isLoading: isPraiseCountsLoading } = useInteractionsCounts(
        {
            ...groupByFilters,
            hasPraise: true,
        },
        { select: indexByGroup }
    );

    const { data: grammarCounts, isLoading: isGrammarCountsLoading } = useInteractionsCounts(
        {
            ...groupByFilters,
            writingIssueType: WritingIssueType.Grammar,
        },
        {
            enabled: GRAMMAR_METRIC_ENABLED,
            select: indexByGroup,
        }
    );

    const { data: spellingCounts, isLoading: isSpellingCountsLoading } = useInteractionsCounts(
        {
            ...groupByFilters,
            writingIssueType: WritingIssueType.Spelling,
        },
        { enabled: SPELLING_METRIC_ENABLED, select: indexByGroup }
    );

    const enableMultipleCounts =
        Array.isArray(tagSelected) && tagSelected.length > 0 && tagSelected.every((tag) => !isValueEmpty(tag));

    // Get all query tags including filter tags for each selection
    const lookupQueryTags = useLookupQueryTags(filters.tagId);
    const multipleTagCounts = useInteractionsMultipleCounts(lookupQueryTags, groupByFilters, {
        enabled: enableMultipleCounts,
    });

    // Calculate overall percentage tag value for selected tags
    const tagsPercentageValues = multipleTagCounts.reduce<any[]>((values, { data }) => {
        return [...values, indexByGroup(data ?? [])];
    }, []);

    const isMultipleTagCountsLoading = useMemo(
        () => multipleTagCounts.some(({ isLoading }) => isLoading),
        [multipleTagCounts]
    );

    const data = useMemo(() => {
        if (groups === null || typeof metricsSummaries === "undefined") {
            return undefined;
        }

        const formattedData = formatData(
            metricsSummaries,
            counts,
            groups,
            getGroupId,
            frustrationCounts,
            praiseCounts,
            grammarCounts,
            spellingCounts,
            tagsPercentageValues
        );

        return formattedData;
    }, [
        frustrationCounts,
        getGroupId,
        grammarCounts,
        praiseCounts,
        spellingCounts,
        counts,
        metricsSummaries,
        groups,
        tagsPercentageValues,
    ]);

    return {
        data,
        isLoading:
            groups === null ||
            isMetricsSummariesLoading ||
            isCountsLoading ||
            isFrustrationCountsLoading ||
            isPraiseCountsLoading ||
            isGrammarCountsLoading ||
            isSpellingCountsLoading ||
            isMultipleTagCountsLoading,
    };
};

/**
 * Request all the data needed for agent comparison and format it.
 * @returns An object containing per agent data and overall data.
 */
export const useComparisonData = <Group>(
    groups: Group[] | null,
    getGroupId: (group: Group) => string,
    groupById: string
) => {
    const filters = useInteractionQueryParameters();

    const { data: overallComparisonData, isLoading: isOverallComparisonDataLoading } =
        useOverallComparisonDataQuery(filters);

    const { data: comparisonData, isLoading: isComparisonDataLoading } = useComparisonDataQuery(
        groups,
        groupById,
        getGroupId,
        filters
    );

    return {
        comparisonData,
        isComparisonDataLoading: isComparisonDataLoading || isOverallComparisonDataLoading,
        overallComparisonData,
    };
};
